import { useEffect, useRef, useState, useMemo } from "react";

import { AgGridReact } from "ag-grid-react";
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Box from "@mui/material/Box"
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'

import { useDispatch } from "react-redux";
import { useTranslation, withTranslation } from "react-i18next";
import { isEmpty, replace, size, cloneDeep, isNull, isUndefined, isEqual } from "lodash";

import salesTxnService from "@plannel/services/data/sales-txn-service";
import locationService from "@plannel/services/data/location-service"
import PaginationContainer from "@plannel/components/PaginationContainer"
import Dialog from "@plannel/components/Dialog"
import ExcelExportButton from "@plannel/components/ExcelExportButton"
import ItemAutocomplete from "@plannel/components/filter/ItemAutocomplete";
import CustomerAutocomplete from "@plannel/components/filter/CustomerAutocomplete";
import Snackbar from "@plannel/components/Snackbar";
import DataState from "@plannel/components/aggrid/DataState"
import DefaultGridSetting, { gridPaste, nullSafeComparator } from "@plannel/components/aggrid/DefaultGridSetting"
import GridDatePicker from "@plannel/components/GridDatePicker"
import GridUtils from "@plannel/components/aggrid/GridUtils";
import { AddButton, RemoveButton, SaveButton, FilterButton } from "@plannel/components/ActionIconButton";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "../utils/color-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import CodeUtil from "@plannel/pages/utils/code-util";
import NumberUtil from "@plannel/utils/number-util";
import customerService from "@plannel/services/data/customer-service";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";

import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
const CURRENT_DATE = new ZDate();
const START_MIN_DATE = new ZDate(CURRENT_DATE.getFullYear() - 10, 0, 1);
const START_DATE = new ZDate(CURRENT_DATE.getFullYear() - 1, CURRENT_DATE.getMonth(), 1);
const END_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth() + 1, 0);
const initPeriod = { start: START_DATE, end: END_DATE }

const { snackBarMessageColor } = ColorUtil;
const PeriodFilter = ({ period, setPeriod }) => {
  const { t } = useTranslation();

  const onChange = (e, type) => {
    const dateCopy = new Date(e);

    if (type) {
      if (type === "start") {
        dateCopy.setDate(1);
        dateCopy.setHours(0, 0, 0, 0);
      } else if (type === "end") {
        const year = dateCopy.getFullYear();
        const month = dateCopy.getMonth();
        const lastDay = new Date(year, month + 1, 0).getDate();

        dateCopy.setDate(lastDay);
        dateCopy.setHours(23, 59, 59, 999);
      }

      setPeriod({ ...period, [type]: dateCopy });
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} >
      <DatePicker
        label={t("periodStart")}
        views={['year', 'month']}
        format={'yyyy-MM'}
        value={period.start}
        maxDate={period.end ? period.end : END_DATE}
        minDate={START_MIN_DATE}
        onChange={(e) => onChange(e, "start")}
        slotProps={{ textField: { InputProps: { color: 'primary', size: "small", sx: { mr: 1.5, width: 200 } } } }}
      />
      <DatePicker
        label={t("periodEnd")}
        views={['year', 'month']}
        format={'yyyy-MM'}
        value={period.end}
        maxDate={END_DATE}
        minDate={period.start ? period.start : START_MIN_DATE}
        onChange={(e) => onChange(e, "end")}
        slotProps={{ textField: { InputProps: { color: 'primary', size: "small", sx: { mr: 1.5, width: 200 } } } }}
      />
    </LocalizationProvider>
  )
}

const SalesTransactionMaster = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxNotifyState = reduxUtil.getViewState(`${viewName}_NOTIFY`);
  const reduxDispatch = useDispatch();
  const showLimitSnackbar = useRef(reduxNotifyState ? reduxNotifyState?.showLimitSnackbar : true);
  const userData = JSON.parse(localStorage.getItem("user"));

  const pageSizes = [100, 200, 500];
  const [resultTotalCount, setResultTotalCount] = useState(null);
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ?? 1);
  const [tPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ?? pageSizes[0]);
  const [sortParams, setSortParams] = useState({});
  const [locationSearch, setLocationSearch] = useState(reduxViewState?.locationSearch ?? null);
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);
  const customerSearch = useRef(reduxViewState?.customerSearch ?? "");
  const extraCustomerCodesRef = useRef(reduxViewState?.extraCustomerCodes ?? []);
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");

  const [period, setPeriod] = useState(reduxViewState?.start && reduxViewState?.end ? { start: reduxViewState.start, end: reduxViewState.end } : initPeriod);
  const [limitInfo, setLimitInfo] = useState({});
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);

  const [salesTxns, setSalesTxns] = useState([]);
  const [locations, setLocations] = useState([]);
  const [csvColDef, setCsvColDef] = useState([]);
  const [customers, setCustomers] = useState([]);
  const currencyMap = useRef({});
  const EXCEL_EXPORT = "excelExport";

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getRequestParams = () => {
    const params = {};
    const searchFilters = {};

    if (!isEmpty(locationSearch)) {
      searchFilters.locCd = locationSearch.locCd;
    }

    if (extraCustomerCodesRef.current?.length > 0) {
      searchFilters.customers = [customerSearch.current, ...extraCustomerCodesRef.current].filter(Boolean);
    } else if (customerSearch.current) {
      searchFilters.customer = customerSearch.current;
    }

    if (extraItemCodesRef.current?.length > 0) {
      searchFilters.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      searchFilters.item = itemSearch.current;
    }

    if (period?.start && period?.end) {
      searchFilters.startDate = period.start;
      searchFilters.endDate = period.end;
    }

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    if (currentPage) {
      params.page = currentPage - 1;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }

    params.searchFilters = searchFilters;

    return { ...params, ...sortParams };
  };

  const retrieveCustomers = () => {
    customerService.getLookup()
      .then(response => {
        const { results } = response.data;
        setCustomers(results);
      })
      .catch(e => {
        debug(e);
      })
  };

  const retrieveSalesTxn = () => {
    const params = getRequestParams();
    salesTxnService.getAll(params)
      .then(response => {
        const { results, totalPages, totalItems, isLimit, limitSize, currency } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        } else {
          setLimitInfo({ isLimit, limitSize });
        }

        setSalesTxns(results);
        setTotalPages(totalPages);
        setResultTotalCount(totalItems);
        if (size(currency)) {
          currencyMap.current = currency;
        }
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, customerSearch: customerSearch.current, extraCustomerCodes: extraCustomerCodesRef.current, locationSearch, currentPage, pageSize, ...period, advancedFilters });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
        DataState.initialize(gridRef.current?.api);
      })
  };

  const refreshGrid = () => {
    retrieveSalesTxn();
  };

  const retrieveLocation = () => {
    locationService.getAll({ pagination: false })
      .then(response => {
        setLocations(response.data.results);
      })
      .catch(e => {
        debug(e);
      })
  }

  const dropdownValueFormatter = (data) => {
    if (typeof data?.value === "string") {
      return data.value;
    }
    return data.value ? data.value.name : "";
  }

  const locationCdValueFormatter = (location) => {
    const formatter = location?.value?.locCd;
    return formatter ?? "";
  }

  const locationNmValueFormatter = (location) => {
    const formatter = location?.value?.name;
    return formatter ?? "";
  }

  const objectComparator = (value1, value2, property, isDescending) => {
    const val1 = !value1 ? "" : value1[property];
    const val2 = !value2 ? "" : value2[property];
    
    return nullSafeComparator(val1, val2, isDescending);
  }

  const currencyRenderer = (param, isExport = false) => {
    const locCd = param?.data?.location?.locCd;
    let symbol = userData?.currencySymbol;
    if (size(Object.keys(currencyMap?.current))) {
      const currencyCd = currencyMap.current[locCd];
      if (currencyCd) {
        symbol = CodeUtil.getCurrencySymbol(currencyCd);
      }
    }
    const value = param?.data?.[param?.colDef?.field];
    if (isNull(value) || isUndefined(value)) {
      return "";
    }
    const formatValue = NumberUtil.formatNumber(value, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
    if (isExport) {
      return formatValue.replace(/,/g, "");
    }
    if (value < 0) {
      return `-${symbol}${replace(formatValue, "-", "")}`;
    }
    return symbol + formatValue;
  };
  currencyRenderer.customType = EXCEL_EXPORT

  const onCellValueChanged = (params) => {
    gridPaste(params);

    const { column, node, value } = params;
    const colId = column.getColId();
    if (isEqual(colId, "customerNm")) {
      const filterVal = customers.filter(arr => arr.name === value)
      if (filterVal.length > 0) {
        node.data.customerCd = filterVal[0].customerCd;
        node.data.customerDto = filterVal[0]
      }
    }
    DataState.setDataState(params);
  }

  const columnDefs = [
    {
      headerName: "locCd", field: "location", colId: "locCd",
      type: ['nonEditableColumn'],
      cellClass: 'stringType',
      valueFormatter: locationCdValueFormatter,
      comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, "locCd", isDescending),
      width: 180, filterType: "string"
    },
    {
      headerName: "locName", field: "location", colId: "locNm",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      valueFormatter: locationNmValueFormatter,
      cellEditorParams: {
        values: locations
      },
      comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, "name", isDescending),
      width: 200, 
      filterType: "string",
    },
    { headerName: "itemCd", field: "itemCd", width: 180, cellClass: 'stringType', filterType: "string" },
    { headerName: "itemName", field: "itemNm", type: ["nonEditableColumn"], width: 250, filterType: "string" },
    {
      headerName: "customerCd",
      field: "customerCd",
      minWidth: 150,
      type: ["nonEditableColumn"],
      flex: 2,
      cellClass: 'stringType',
      width: 180, filterType: "string"
    },
    {
      headerName: "customerName", field: "customerNm",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      valueFormatter: dropdownValueFormatter,
      cellEditorParams: {
        valueFormatter: dropdownValueFormatter,
        values: customers?.map(customer => customer.name)
      },
      comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, "name", isDescending),
      minWidth: 150,
      flex: 2,
      filterType: "string",
    },
    { 
      headerName: "txnDate", field: "txnDate",
      type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" }, width: 160,
      filterType: "date", valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd") 
    },
    { headerName: "salesQty", field: "salesQty", type: ["numberColumn", "rightAligned"], width: 120, filterType: "number" },
    { headerName: "salesAmt", field: "salesAmt", type: ["numberColumn", "rightAligned"], valueFormatter: currencyRenderer, width: 120, filterType: "number" },
    { headerName: "cogs", field: "cogs", type: ["numberColumn", "rightAligned"], valueFormatter: currencyRenderer, width: 120, filterType: "number" },
    { headerName: "createdTs", field: "createdTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, filterType: 'timestamp', width: 170 },
    { headerName: "createdBy", field: "createdUser", colId: "createdBy", type: "nonEditableColumn", width: 150, filterType: 'string'},
    { headerName: "updatedTs", field: "updatedTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, filterType: 'timestamp' },
    { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", type: "nonEditableColumn", width: 150, filterType: 'string'}
  ];

  const insertRow = () => {
    const newRow = {
      id: null,
      location: null,
      itemCd: null,
      itemNm: null,
      customerCd: null,
      customerNm: null,
      txnDate: null,
      salesQty: null,
      salesAmt: null,
      cogs: null,
      verNum: null
    };

    const addRow = gridRef.current.api.applyTransaction({ add: [newRow], addIndex: 0 }).add[0];

    DataState.setDataState(addRow);
  };

  const removeSalesTxn = () => {
    const salesTxnIds = [];

    gridRef.current.api.getSelectedRows().forEach(
      (data) => {
        salesTxnIds.push(data.id)
      }
    );

    if (salesTxnIds.length > 0) {
      salesTxnService.remove(salesTxnIds)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
        })
        .then(() => {
          refreshGrid();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const saveSalesTxn = () => {
    const allStateData = DataState.getAllStateData(gridRef.current.api);

    if (allStateData.length > 0) {
      salesTxnService.upsert(allStateData)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        })
        .catch(e => {
          const { message } = e.response?.data;
          if (message) {
            setSnackInfo({ open: true, severity: message.type, content: t(message.message) });
          } else {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          }
          debug(e);
        })
        .then(() => {
          refreshGrid();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const handleDialog = (action) => {
    if (action === "SAVE") {
      saveSalesTxn();
    } else if (action === "DELETE") {
      removeSalesTxn();
    }
  }

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveSalesTxn();
    }
  }

  const onGridReady = (params) => {
    retrieveLocation();
    retrieveCustomers();

    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
    setCsvColDef(cloneDeep(columnDefs))
  }

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };

  useEffect(() => {
    if (csvColDef.isEmpty) {
      return;
    }
    for (const col of csvColDef) {
      if (col.valueFormatter?.customType === EXCEL_EXPORT) {
        col.cellRenderer = null;
        col.valueFormatter = null;
        col.valueGetter = params => currencyRenderer(params, true);
        col.cellClass = params => {
          const value = Number(params.value);
          return !isNaN(value) && !Number.isInteger(value) ? 'flexibleDecimalType' : '';
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csvColDef])

  useEffect(() => {
    if (gridLoading) {
      DataState.initialize(gridRef.current?.api);
      retrieveSalesTxn();
    }

    return () => {
      setSalesTxns([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams]);

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }

    return () => {
      setSalesTxns([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters, locationSearch, period]);

  useEffect(() => {
    if (limitInfo?.isLimit && showLimitSnackbar.current) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.exportLimit, content: `${t("MSG_LimitedSearchResults", { N: limitInfo.limitSize })}` });
      reduxDispatch(reduxUtil.updateViewState({ viewName: `${viewName}_NOTIFY`, showLimitSnackbar: false }));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limitInfo?.isLimit, showLimitSnackbar.current]);

  useEffect(() => {
    if (!isEmpty(locations)) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations]);

  useEffect(() => {
    if (!isEmpty(customers)) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers]);

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  const statusBar = useMemo(() => {
    return {
      statusPanels: [
        { statusPanel: "agTotalRowCountComponent", align: "center" },
        { statusPanel: "agAggregationComponent" }
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <FilterContainer>
        <PeriodFilter period={period} setPeriod={setPeriod} />
        <Autocomplete
          options={locations}
          getOptionLabel={option => option.name}
          value={locationSearch}
          onChange={(_, newValue) => setLocationSearch(newValue)}
          isOptionEqualToValue={(option, value) => option.locCd === value.locCd}
          size="small"
          sx={{ minWidth: 200, mr: 1 }}
          renderInput={(params) => (
            <TextField {...params} label={t("location")} />
          )}
        />
        <CustomerAutocomplete retrieve={initPageToRetrieve} searchValue={customerSearch} useAccessFilter={false} sx={{ mr: 1 }} extraCodesRef={extraCustomerCodesRef} />
        <ItemAutocomplete retrieve={initPageToRetrieve} searchValue={itemSearch} useAccessFilter={false} sx={{ mr: 1 }} extraCodesRef={extraItemCodesRef} />
        <Box sx={{ ml: "auto" }}>
          <ExcelExportButton title={title} service={salesTxnService} columnDefs={csvColDef} totalRows={resultTotalCount}
            params={getRequestParams()} />
          <AddButton onClick={insertRow} />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
          <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </FilterContainer>
      <Box id="sales-transaction-master-grid" className="ag-theme-balham grid-area" mb={1}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%', mr: 1.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <AgGridReact
              ref={gridRef}
              rowData={salesTxns}
              {...defaultGridMemo}
              rowSelection={{
                ...defaultGridMemo.rowSelection,
                headerCheckbox: true,
                checkboxes: true,
              }}
              onGridReady={onGridReady}
              statusBar={statusBar}
              onCellValueChanged={onCellValueChanged}
              onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
            />
          </Box>
          {openFilter && (
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={columnDefs}
                advancedFilters={advancedFilters}
                onApply={setAdvancedFilters}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          )}
        </Box>
      </Box>
      <PaginationContainer
        currentPage={currentPage} totalPages={tPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
        disableButton={dialogInfo.disableButton}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(SalesTransactionMaster);